/**
 * Cache Service for storing and retrieving data with TTL (Time To Live)
 * Uses localStorage for persistent caching across sessions
 */

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_VERSION = 'v1'; // Increment this to invalidate all caches
const MAX_CACHE_SIZE = 5 * 1024 * 1024; // 5MB max cache size

class CacheService {
  constructor() {
    this.prefix = `fabric_cache_${CACHE_VERSION}_`;
  }

  /**
   * Generate cache key with prefix
   */
  getCacheKey(key) {
    return `${this.prefix}${key}`;
  }

  /**
   * Set data in cache with TTL
   */
  set(key, data, ttl = DEFAULT_TTL) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cacheData = {
        data,
        timestamp: Date.now(),
        ttl,
        version: CACHE_VERSION
      };
      
      const serialized = JSON.stringify(cacheData);
      
      // Check if adding this would exceed max cache size
      if (this.getCacheSize() + serialized.length > MAX_CACHE_SIZE) {
        this.cleanOldest();
      }
      
      localStorage.setItem(cacheKey, serialized);
      return true;
    } catch (error) {
      console.warn('Error setting cache:', error);
      // If quota exceeded, try cleaning old cache and retry
      if (error.name === 'QuotaExceededError') {
        this.cleanOldest();
        try {
          localStorage.setItem(this.getCacheKey(key), JSON.stringify({
            data,
            timestamp: Date.now(),
            ttl,
            version: CACHE_VERSION
          }));
          return true;
        } catch (retryError) {
          console.error('Failed to cache even after cleanup:', retryError);
        }
      }
      return false;
    }
  }

  /**
   * Get data from cache if not expired
   */
  get(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }
      
      const cacheData = JSON.parse(cached);
      
      // Check version
      if (cacheData.version !== CACHE_VERSION) {
        this.delete(key);
        return null;
      }
      
      // Check if expired
      const now = Date.now();
      const age = now - cacheData.timestamp;
      
      if (age > cacheData.ttl) {
        this.delete(key);
        return null;
      }
      
      return cacheData.data;
    } catch (error) {
      console.warn('Error getting cache:', error);
      return null;
    }
  }

  /**
   * Delete specific cache entry
   */
  delete(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      localStorage.removeItem(cacheKey);
      return true;
    } catch (error) {
      console.warn('Error deleting cache:', error);
      return false;
    }
  }

  /**
   * Remove all cache entries whose logical key starts with the given prefix
   * (e.g. "collection_" clears collection_abc_true, collection_xyz_false, …)
   */
  deleteMatchingLogicalPrefix(logicalPrefix) {
    try {
      const fullPrefix = this.getCacheKey(logicalPrefix);
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(fullPrefix)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.warn('Error deleting cache by prefix:', error);
      return false;
    }
  }

  /**
   * Clear all cache entries with our prefix
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.warn('Error clearing cache:', error);
      return false;
    }
  }

  /**
   * Get total size of cache
   */
  getCacheSize() {
    try {
      let totalSize = 0;
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            totalSize += item.length;
          }
        }
      });
      return totalSize;
    } catch (error) {
      console.warn('Error calculating cache size:', error);
      return 0;
    }
  }

  /**
   * Clean oldest cache entries to free up space
   */
  cleanOldest() {
    try {
      const keys = Object.keys(localStorage);
      const cacheEntries = [];
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const data = JSON.parse(item);
              cacheEntries.push({
                key,
                timestamp: data.timestamp
              });
            }
          } catch (e) {
            // Invalid entry, remove it
            localStorage.removeItem(key);
          }
        }
      });
      
      // Sort by timestamp (oldest first)
      cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest 30% of entries
      const toRemove = Math.ceil(cacheEntries.length * 0.3);
      for (let i = 0; i < toRemove && i < cacheEntries.length; i++) {
        localStorage.removeItem(cacheEntries[i].key);
      }
      
      return true;
    } catch (error) {
      console.warn('Error cleaning oldest cache:', error);
      return false;
    }
  }

  /**
   * Clean expired cache entries
   */
  cleanExpired() {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const data = JSON.parse(item);
              const age = now - data.timestamp;
              
              if (age > data.ttl || data.version !== CACHE_VERSION) {
                localStorage.removeItem(key);
              }
            }
          } catch (e) {
            // Invalid entry, remove it
            localStorage.removeItem(key);
          }
        }
      });
      
      return true;
    } catch (error) {
      console.warn('Error cleaning expired cache:', error);
      return false;
    }
  }

  /**
   * Check if a key exists in cache and is not expired
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    try {
      const keys = Object.keys(localStorage);
      let totalEntries = 0;
      let expiredEntries = 0;
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          totalEntries++;
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const data = JSON.parse(item);
              const age = now - data.timestamp;
              if (age > data.ttl) {
                expiredEntries++;
              }
            }
          } catch (e) {
            // Invalid entry
            expiredEntries++;
          }
        }
      });
      
      return {
        totalEntries,
        expiredEntries,
        validEntries: totalEntries - expiredEntries,
        totalSize: this.getCacheSize(),
        maxSize: MAX_CACHE_SIZE,
        usagePercent: ((this.getCacheSize() / MAX_CACHE_SIZE) * 100).toFixed(2)
      };
    } catch (error) {
      console.warn('Error getting cache stats:', error);
      return null;
    }
  }
}

// Create singleton instance
const cacheService = new CacheService();

// Clean expired cache on load
cacheService.cleanExpired();

export default cacheService;
